import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { AuthContext } from './AuthContext';
import type { User } from '../types';

const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // refresh 5 min before token expires

const DEV_USER: User = {
  email: process.env.REACT_APP_DEV_USER_EMAIL || 'dev.user@fivetran.com',
  name: 'Dev User',
  userId: 'dev-user-id',
  provider: 'dev-mode',
};

/** Decode the `exp` claim from a JWT without verifying signature. */
function jwtExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback((accessToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const expiry = jwtExpiry(accessToken);
    if (!expiry) return;
    const delay = Math.max(expiry - Date.now() - REFRESH_BEFORE_EXPIRY_MS, 0);
    refreshTimerRef.current = setTimeout(async () => {
      const refresh = localStorage.getItem('refresh_token');
      if (!refresh) return;
      try {
        const r = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refresh }),
        });
        if (!r.ok) throw new Error('refresh failed');
        const { accessToken, refreshToken } = await r.json();
        localStorage.setItem('access_token', accessToken);
        // Store the rotated refresh token issued by the server
        if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
        scheduleRefresh(accessToken);
      } catch {
        // refresh failed — force re-login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        window.location.href = '/login';
      }
    }, delay);
  }, []);

  useEffect(() => {
    if (isDevMode) {
      setUser(DEV_USER);
      setIsLoading(false);
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };

    fetch(`${API_URL}/auth/user`, { headers })
      .then(r => {
        if (!r.ok) throw new Error('not authenticated');
        return r.json();
      })
      .then((u: User) => {
        setUser(u);
        scheduleRefresh(token);
      })
      .catch(() => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      })
      .finally(() => setIsLoading(false));

    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, [scheduleRefresh]);

  const login = useCallback((provider: 'google' | 'okta') => {
    window.location.href = `${API_URL}/auth/${provider}`;
  }, []);

  const logout = useCallback(async () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const accessToken  = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (accessToken) {
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          // Send refresh token so the server can blacklist it too
          body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        });
      } catch {
        // continue with local logout even if API fails
      }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
